import type { MessageKey } from "./ko";

/** English messages. `{n|one|other}` picks a word form by the value of `n`. */
export const en: Record<MessageKey, string> = {
  // Shared
  "common.retry": "Try again",
  "common.otherRepo": "Other repo",
  "common.close": "Close",
  "common.closeEsc": "Close (Esc)",
  "common.signOut": "Sign out",
  "common.open": "Open",
  "common.cancel": "Cancel",

  // Experiment status, warnings, seasons
  "status.running": "Running",
  "status.adopted": "Adopted",
  "status.rejected": "Rejected",
  "status.draft": "Draft",
  "warning.no-yaml-block": "The PR body has no YAML block.",
  "warning.yaml-parse-error": "The YAML block could not be parsed. Check its format.",
  "warning.missing-hypothesis": "The hypothesis field is empty.",
  "warning.invalid-status": "status is not one of running / adopted / rejected.",
  "warning.invalid-field": "Some fields had an invalid format and were ignored.",
  "warning.orphan": "The parent experiment was not found, so this is shown under research.",
  "warning.cycle": "The parent chain forms a cycle, so this is shown under research.",
  "warning.unknown-version": "The research version tag in parent was not found, so this is shown under research.",
  "season.spring": "Spring",
  "season.summer": "Summer",
  "season.autumn": "Autumn",
  "season.winter": "Winter",

  // App shell and screens
  "app.loading": "Loading…",
  "app.loadingRepo": "Loading {repo}…",
  "app.connectFailedTitle": "Can't connect to GitHub",
  "app.connectFailedBody": "Check your network connection and try again.",
  "app.sessionExpired": "Your sign-in has expired. Please sign in again.",
  "app.repoNotFound": "{repo} was not found, or you don't have access to it.",
  "app.treeFailedTitle": "Couldn't load the tree",
  "app.invalidRepo": "Not in owner/name format.",
  "app.noRootTitle": "No {root} branch",
  "app.noRootBody": "ResearchTree uses the {root} branch as the root of the research tree.",
  "app.noRootNext": "Then record each experiment as a PR from a {prefix}<name> branch. If you use different names, change the branch settings.",
  "app.recheck": "Check again",
  "app.refreshFailed": "Refresh failed: {error}",
  "app.refreshed": "Up to date.",
  "app.switchFailed": "Couldn't switch views: {error}",
  "app.noWebGL": "This browser doesn't support WebGL, so the tree can't be drawn.",
  "app.repoInfo": "Repository info",
  "app.detail": "Experiment details",
  "app.account": "Account",
  "hint.3d": "Drag to pan · Right-drag to rotate · Scroll to zoom · Arrow keys to move between nodes · Esc to close",
  "hint.2d": "Drag to pan · Scroll to zoom · Arrow keys to move between nodes · Esc to close",
  "menu.openRepoOnGitHub": "Open this repo on GitHub",
  "menu.myIsland": "Open my island",
  "menu.openOtherRepo": "Open another repo",
  "menu.guide": "User guide",
  "menu.project": "ResearchTree repository",
  "toolbar.refresh": "Refresh",
  "toolbar.settings": "Settings",
  "toolbar.fit": "Fit all",
  "toolbar.flat": "Flat view",
  "toolbar.tree": "Tree view",
  "toolbar.raise": "3D view",
  "toolbar.island": "Island view",
  "toolbar.switchMode": "Switch view: island → flat → tree → 3D tree",
  "brand.collapse": "Collapse card",
  "brand.expand": "Expand card",
  "brand.layers": "Status layers",
  "brand.showStatus": "Show {status} experiments",
  "brand.dimStatus": "Dim {status} experiments",
  "brand.subVersions": "{root} · {count} {count|version|versions} · {depth} {depth|generation|generations}",
  "brand.subBranches": "{count} {count|branch|branches} from {root} · {depth} {depth|generation|generations}",
  "brand.empty": "No {prefix}* PRs yet.",
  "brand.emptyNext": "Branch your first experiment off {root} and open a PR.",
  "gens.nav": "Generations",
  "gens.label": "Gen",
  "gens.prev": "Previous generation",
  "gens.next": "Next generation",
  "gens.all": "All {n}",

  // Sign-in
  "login.intro": "Reads your experiment branches and PRs and shows how your research grew as a tree. You only need to sign in to GitHub once.",
  "login.withGitHub": "Sign in with GitHub",
  "login.deviceCodePrompt": "Enter the code below on GitHub and approve.",
  "login.openGitHub": "Open GitHub",
  "login.deviceAuto": "You'll continue automatically once approved.",
  "login.tokenLabel": "Personal access token",
  "login.withToken": "Sign in with token",
  "login.tokenInvalid": "Couldn't verify the token. Check its permissions and expiry date.",
  "login.tokenHelp": "For a fine-grained token, grant Pull requests (read/write) and Contents (read) on the target repos. The token is stored only in this browser.",
  "login.patSummary": "Sign in with a personal access token (PAT)",

  // Repo picker
  "picker.title": "Which research do you want to see?",
  "picker.body": "Pick a repo with a research branch and experiment/* PRs.",
  "picker.repoLabel": "Repository",
  "picker.loading": "Loading repositories…",
  "picker.noMatch": "No matching repositories.",
  "picker.private": "Private",
  "picker.listFailed": "Couldn't load your repositories. Type one in.",
  "picker.formatError": "Enter it as owner/name.",
  "picker.recent": "Recently viewed",
  "picker.mine": "Your repositories",
  "picker.demo": "Explore the demo repository",
  "picker.demoHint": "A made-up year of text-to-speech research: five versions, competing hypotheses side by side, and intent and spec documents.",

  // Settings
  "settings.title": "Settings",
  "settings.language": "Language",
  "settings.languageAuto": "Auto ({source})",
  "settings.sourceBrowser": "browser language",
  "settings.sourceVscode": "VS Code language",
  "settings.languageNote": "The language applies to every repository.",
  "settings.branchTitle": "Branches",
  "settings.branchBody": "The root branch and the experiment prefix come from the repository's .researchtree. One file per repository, so everyone who opens it sees the same tree.",
  "settings.save": "Save and reload",

  // Detail panel
  "panel.path": "Path",
  "panel.versionEyebrow": "Research · Version",
  "panel.version": "Version",
  "panel.tagged": "Tagged {date}",
  "panel.mergedSection": "Merged experiments · what made this version",
  "panel.noMerged": "No adopted experiments linked to this version were found.",
  "panel.versionMetrics": "Metrics of this version",
  "panel.startedHere": "Experiments started from this version",
  "panel.noStarted": "No experiments have started from this version yet.",
  "panel.nextVersion": "Next version",
  "panel.viewOnGitHub": "View on GitHub",
  "panel.started": "started {date}",
  "panel.merged": "merged {date}",
  "panel.closed": "closed {date}",
  "panel.tabSummary": "Overview",
  "panel.tabMetrics": "Metrics",
  "panel.tabCollab": "Discussion",
  "panel.tabFiles": "Files",
  "panel.edit": "Edit",
  "panel.editTitle": "Edit the lab note",
  "panel.editHint": "In the YAML block only the fields above change; other fields, comments and key order stay as they are. The body is everything below the YAML block.",
  "panel.fieldHypothesis": "Hypothesis",
  "panel.fieldChange": "Change",
  "panel.fieldStatus": "Status",
  "panel.statusAuto": "Auto (follow the PR state)",
  "panel.fieldTags": "Tags (comma-separated)",
  "panel.fieldWandb": "W&B link",
  "panel.fieldBody": "Body (markdown: conclusion, notes, …)",
  "panel.badUrl": "The W&B link must start with http:// or https://.",
  "panel.save": "Save",
  "panel.saving": "Saving…",
  "panel.saved": "Saved the PR body.",
  "panel.saveFailed": "Could not save: {error}",
  "panel.conflict": "The PR body changed on GitHub in the meantime. Refresh, then edit again.",
  "panel.commentsLoading": "Loading comments…",
  "panel.noComments": "No comments yet.",
  "panel.commentsFailed": "Could not load comments: {error}",
  "panel.commentPlaceholder": "Leave a comment (markdown)",
  "panel.commentSubmit": "Comment",
  "panel.commentPosting": "Posting…",
  "panel.commentFailed": "Could not post the comment: {error}",
  "panel.reviewOn": "Review on {path}",
  "panel.filesLoading": "Loading changes…",
  "panel.noFiles": "No files changed.",
  "panel.filesFailed": "Could not load changes: {error}",
  "panel.filesSummary": "{n} {n|file|files} · +{add} −{del}",
  "panel.noPatch": "No preview (binary or too large).",
  "panel.patchTruncated": "Showing the first {n} lines.",
  "panel.fileAdded": "added",
  "panel.fileRemoved": "removed",
  "panel.fileModified": "modified",
  "panel.fileRenamed": "renamed",
  "panel.viewFile": "View on GitHub",
  "panel.tabCommits": "Commits",
  "panel.hypothesis": "Hypothesis",
  "panel.noHypothesis": "No hypothesis written.",
  "panel.change": "Change",
  "panel.record": "Results",
  "panel.openOnGitHub": "Open on GitHub",
  "panel.wandb": "Training log",
  "panel.checkout": "Checkout",
  "panel.diff": "Diff vs parent",
  "panel.children": "Derived experiments",
  "panel.noChildren": "No experiments have been derived from this one yet.",
  "panel.noMetrics": "No metrics recorded. Add values under metrics in the PR body YAML.",
  "panel.metric": "Metric",
  "panel.thisExperiment": "This",
  "panel.parent": "Parent",
  "panel.deltaVersion": "Change vs {parent} (based on its last merged experiment).",
  "panel.deltaParent": "Change vs the parent experiment ({parent}).",
  "panel.noParentMetrics": "This experiment started from the first research version, so there are no parent metrics to compare.",
  "panel.commitsLoading": "Loading commits…",
  "panel.noCommits": "No commits.",
  "panel.commitsFailed": "Couldn't load commits: {error}",

  // Tree view
  "tree.versionSub": "{date} · {n} merged {n|experiment|experiments}",
  "tree.rootSub": "Root of the research",
  "panel.pathExpand": "Show the full path",
  "panel.pathCollapse": "Collapse the path",
  "panel.resize": "Resize panel (drag or arrow keys)",
  "panel.labelMetric": "Label metric",
  "panel.labelMetricHint": "Metric shown on the experiment labels of this island. Each version has its own.",
  "panel.labelMetricNone": "None",

  // Season gallery (guide)
  "gallery.title": "Season gallery",
  "gallery.sub": "One adopted, running, rejected and draft experiment per season. Trees take their branch color; seasons show as blossoms, fireflies, fallen leaves and snow; the ground follows the season at its place on the time axis.",
  "gallery.closeUp": "Each season up close",
  "gallery.caption": "{season} · Adopted · Running · Rejected · Draft",
  "gallery.yearIsland": "A whole year: 3D islands",
  "gallery.island": "3D islands",
  "gallery.yearFlat": "A whole year: flat view",
  "gallery.hypothesis": "{state} in {season}",

  // Hosts
  "web.stateMismatch": "The sign-in request didn't match. Please try again.",
  "web.proxyUnreachable": "Can't reach the authentication server.",
  "web.noToken": "No token was received.",
  "web.noClientId": "The OAuth App client ID is not configured (VITE_GITHUB_CLIENT_ID).",
  "web.noClientIdShort": "The OAuth App client ID is not configured.",

  // Research intro reef, beside research v1
  "intro.reef": "About this research",
  "intro.reefSub": "what this research is",
  "intro.title": "About this research",
  "intro.overview": "Overview",
  "intro.project": "Project goal",
  "intro.research": "Research goal",
  "intro.intent": "Intent",
  "intro.spec": "Spec",
  "intro.empty": "No documents to read. Put a README.md on the root branch and it shows up here.",
  "intro.loading": "Reading the documents…",
  "intro.failed": "Couldn't read the documents: {error}",
  "intro.source": "{path} at {ref}",

  // Island (docs/ISLAND.md): one map for everything an account researches
  "world.title": "{user}'s research",
  "world.researchCount": "{count} research",
  "world.portal": "Sail to {name}",
  "world.portalSub": "portal",
  "world.back": "Back to the map",
  "world.map": "Map",
  "world.mapTitle": "Research map",
  "world.mapHint": "Click to sail there · double-click to open the research · {count} research",
  "world.empty": "No research on the map yet. `researchtree island add` puts some there.",
  "world.noSettings": "{user} hasn't made a ResearchIsland yet.",
  "world.noSettingsBody": "Share it with {user} and invite them to join ResearchIsland!",
  "world.noSettingsHow": "A .researchisland repository on the account, with the README saying which research goes where, is all this map needs.",
  "world.share": "Share",
  "world.shareCopied": "Invitation copied",
  "world.shareFailed": "Couldn't share it",
  "share.inviteTitle": "ResearchTree — the island your research grows on",
  "share.inviteText":
    "{user}, leave your research as an island on ResearchTree. One branch is one experiment, one PR is its lab note, and the notes grow into trees on the island.\nHow to start: {guide}\nWhere {user}'s island will open:",
  "world.failed": "Couldn't read the map: {error}",
  "world.land": "Island",
  "hint.world": "Drag to move · wheel to zoom · click an island to open it",
  "world.lockedName": "Locked island",
  "world.lockedSub": "Sign in with an account that has access",
  "world.lockedTitle": "{repo}: you need access to this one",
  "world.locked": "Can't open {repo}. A private repository needs an account with access to it.",

  // The researcher: their introduction and résumé
  "profile.open": "About the researcher",
  "profile.resume": "Résumé",
  "profile.title": "{user}",
  "profile.fromSettings": "PROFILE.md",
  "profile.fromAccount": "GitHub profile README",

  // The scroll that names where you just arrived
  "banner.research": "Research",

  // Board view: the stages a PR actually goes through
  "board.draft": "Draft",
  "board.working": "In progress",
  "board.review": "In review",
  "board.merged": "Adopted",
  "board.closed": "Rejected",
  "board.empty": "Nothing here",
  "toolbar.board": "Board",
  "toolbar.boardOff": "Back to the island",

  // Guest mode (reading a public repo without signing in)
  "guest.note": "Read-only, because you are not signed in.",
  "guest.noteDates": "Sign in to see when each experiment was worked on (commit dates).",
  "guest.readOnly": "Sign in to edit",
  "guest.commentNeedsSignIn": "Sign in to write a comment.",
  "guest.signInTitle": "Sign in with GitHub for your own repositories and editing",
  "guest.rateLimited": "That is all the anonymous views GitHub allows for now (60 an hour). Signing in gives you plenty.",
  "guest.needsSignIn": "Couldn't open {repo}. A private repository needs a sign-in.",

  // PowerPoint add-in
  "office.signingIn": "Signing in to GitHub…",
  "office.dialogFailed": "Couldn't open the sign-in window.",
  "office.dialogClosed": "The sign-in window was closed.",
  "office.saveView": "Save this view",
  "office.saveViewTitle": "Save what you are looking at with this slide, so the deck opens on this screen next time.",
  "office.viewSaved": "Saved with this slide.",
  "office.saveFailed": "Couldn't save. Settings can't be written during a slideshow.",
  "office.demo": "Explore the demo repository",
  "local.serverError": "Local server error ({status})",
  "local.userFailed": "Signed in, but couldn't load your user info.",
  "local.codeExpired": "The sign-in code has expired. Please try again.",
  "local.denied": "Sign-in was denied on GitHub.",
  "webview.timeout": "The VS Code extension did not respond in time ({type})",

  // Core
  "prbody.parseError": "The YAML block in the PR body could not be parsed, so it was not modified.",
  "prbody.notMap": "The YAML block in the PR body is not in key: value form.",
  "github.invalidPath": "GitHub API path not allowed: {path}",

  // VS Code extension host
  "ext.openTooltip": "Open ResearchTree",
  "ext.signedOut": "ResearchTree: Signed out.",
  "ext.signInCancelled": "GitHub sign-in was cancelled.",
  "ext.invalidToken": "The GitHub token is invalid.",
  "ext.badRequest": "Invalid GitHub request",
  "ext.badMethod": "Method not allowed: {method}",
  "ext.httpOnly": "Only http(s) URLs can be opened.",
  "ext.badBranch": "Invalid branch name",
  "ext.unknownRequest": "Unknown request: {type}",
  "ext.pickRepo": "Pick a repo to view in ResearchTree",
  "ext.noRepo": "No GitHub repository was found in the workspace.",
  "ext.switching": "Switching to {branch}…",
  "ext.switched": "ResearchTree: Switched to {branch}.",
  "ext.checkoutFailed": "ResearchTree: Checkout failed ({branch}) — {error}",
  "ext.compareFailed": "ResearchTree: Couldn't load changed files (GitHub {status}).",
  "ext.noChanges": "ResearchTree: No files changed between {base} and {head}.",
  "ext.changedFiles": "{head} ← {base}: {n} changed {n|file|files}",
  "ext.openCompare": "ResearchTree: Branches not found locally; opening the GitHub compare view.",
  "panel.tabSpec": "Spec",
  "panel.tabOverview": "Overview",
  "panel.claims": "Tests claims",
  "spec.loading": "Loading the spec…",
  "spec.failed": "Couldn't load the spec: {error}",
  "spec.none": "This version has no spec file ({path}). Change the path with spec in the repo's .researchtree.",
  "spec.modeFull": "Full",
  "spec.modeSummary": "Summary",
  "spec.modeChanges": "Changes",
  "spec.modes": "Spec view",
  "spec.added": "Added",
  "spec.changed": "Changed",
  "spec.renamed": "Renamed",
  "spec.removed": "Removed",
  "spec.changedIn": "{kind} in {version}",
  "spec.preamble": "(Preamble)",
  "spec.since": "{n} {n|section|sections} changed since {prev}",
  "spec.noChanges": "No sections changed since {prev}.",
  "spec.firstVersion": "This is the first version, so there is nothing to compare with. Every section starts here.",
  "spec.mergedHere": "Experiments merged into this version",
  "spec.history": "History",
  "spec.historyTitle": "History of this section",
  "spec.historyLoading": "Reading the spec of every version…",
  "spec.historyEmpty": "It never changed in any version.",
  "spec.missing": "Includes that couldn't be read: {paths}",
  "spec.showDiff": "Show the change",
  "spec.expIntro": "Sections this branch changed in the spec, compared with where it forked ({sha}).",
  "spec.expAdopted": "The experiment was adopted, so this change went into research.",
  "spec.expRejected": "The experiment was rejected, so this change never went into research. It stays as a proposal.",
  "spec.expRunning": "The experiment is still running. If it is adopted, this change goes into research.",
  "spec.expNone": "This experiment didn't change the spec.",
  "spec.expNoFile": "This repo has no spec file ({path}).",
  "config.branches": "This repository is read with root {root} and experiments {prefix}*.",
  "config.defaults": "The repository has no settings file, so the defaults are used.",
  "config.repoWarnings": "Ignored in .researchtree: {items}",
};
